import { useEffect, useState } from 'react';
import { Copy, Download, Plus, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SlidePanel } from '@/shared/components';
import { useSuppliers } from '../hooks/useWmsSuppliers';
import {
  copySuppliersToClipboard,
  exportSuppliersCSV,
  useSupplierListTable,
  type SuppliersView,
} from '../hooks/useSupplierListTable';
import { SupplierListView } from '../components/suppliers/SupplierListView';
import { SupplierDetailPanel } from '../components/suppliers/SupplierDetailPanel';
import type { Supplier, SupplierKind, SuppliersResponse } from '../types';

type AccountingTab = 'all' | SupplierKind;

const ACCOUNTING_TABS: { value: AccountingTab; label: string }[] = [
  { value: 'hardware', label: 'Hardware' },
  { value: 'software', label: 'Software' },
  { value: 'service', label: 'Service' },
  { value: 'admin', label: 'Admin' },
  { value: 'carrier', label: 'Carrier' },
  { value: 'other', label: 'Other' },
  { value: 'all', label: 'All' },
];

const ACTIVE_TAB_KEY = 'atek_acc_suppliers_active_tab';
const PANEL_STORAGE_KEY = 'atek_suppliers_panel_width';
const VALID_TABS: AccountingTab[] = ['all', 'hardware', 'software', 'service', 'admin', 'carrier', 'other'];

interface SuppliersPageProps {
  view?: SuppliersView;
}

export default function SuppliersPage({ view = 'wms' }: SuppliersPageProps) {
  const isAccounting = view === 'accounting';

  const [activeTab, setActiveTab] = useState<AccountingTab>(() => {
    if (!isAccounting) return 'hardware';
    const saved = localStorage.getItem(ACTIVE_TAB_KEY) as AccountingTab | null;
    return saved && VALID_TABS.includes(saved) ? saved : 'all';
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [panelMode, setPanelMode] = useState<'view' | 'create'>('view');
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    if (isAccounting) localStorage.setItem(ACTIVE_TAB_KEY, activeTab);
  }, [activeTab, isAccounting]);

  // WMS forces hardware+active. Accounting fetches all statuses (column filter handles it).
  const kindFilter: SupplierKind | undefined = isAccounting
    ? (activeTab === 'all' ? undefined : (activeTab as SupplierKind))
    : 'hardware';

  const suppliersQuery = useSuppliers({
    kind: kindFilter,
    status: isAccounting ? undefined : 'active',
    page_length: 100,
  });
  const suppliersData = suppliersQuery.data as SuppliersResponse | undefined;
  const suppliers = suppliersData?.suppliers ?? [];

  const table = useSupplierListTable(suppliers, view);

  const openSupplier = (s: Supplier) => {
    setSelectedId(s.id);
    setPanelMode('view');
    setPanelOpen(true);
  };

  const openCreate = () => {
    setSelectedId(null);
    setPanelMode('create');
    setPanelOpen(true);
  };

  const handlePanelClose = (open: boolean) => {
    setPanelOpen(open);
    if (!open) {
      setSelectedId(null);
      setPanelMode('view');
    }
  };

  const handleCreated = (id: string) => {
    setSelectedId(id);
    setPanelMode('view');
  };

  const handleCopy = () => {
    copySuppliersToClipboard(table.sortedSuppliers, table.columns, table.ctx);
    toast.success('Copied to clipboard');
  };

  const handleExportCsv = () => {
    exportSuppliersCSV(table.sortedSuppliers, table.columns, table.ctx);
    toast.success(`Exported ${table.sortedSuppliers.length} rows`);
  };

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-between gap-2 border-b border-border flex-wrap">
        <div className="flex items-center gap-0">
          {isAccounting ? (
            ACCOUNTING_TABS.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={cn(
                  'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                  activeTab === tab.value
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                )}
              >
                {tab.label}
              </button>
            ))
          ) : (
            <h1 className="px-4 py-2 text-sm font-semibold">Hardware Suppliers</h1>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap pb-1.5">
          <span className="text-xs text-muted-foreground mr-2">
            {table.sortedSuppliers.length} suppliers
          </span>
          <Button variant="outline" size="sm" onClick={handleCopy}>
            <Copy className="h-4 w-4 mr-1" />
            Copy
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportCsv}>
            <Download className="h-4 w-4 mr-1" />
            CSV
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings2 className="h-4 w-4 mr-1" />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {table.columns.filter((c) => c.label).map((col) => (
                <DropdownMenuCheckboxItem
                  key={col.key}
                  checked={table.isVisible(col.key)}
                  onCheckedChange={() => table.toggleColumn(col.key)}
                >
                  {col.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button size="sm" onClick={openCreate}>
            <Plus className="h-4 w-4 mr-1" />
            New Supplier
          </Button>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex min-h-[calc(100vh-240px)]">
        <div className="flex-1 min-w-0 overflow-auto">
          <SupplierListView
            sortedSuppliers={table.sortedSuppliers}
            visibleColumns={table.visibleColumns}
            columnFilters={table.columnFilters}
            ctx={table.ctx}
            sortKey={table.sortKey}
            sortDirection={table.sortDirection}
            isLoading={suppliersQuery.isLoading}
            onSort={table.handleSort}
            onSetColumnFilter={table.setColumnFilter}
            onSelectSupplier={openSupplier}
            onCreateSupplier={openCreate}
          />
        </div>

        <SlidePanel open={panelOpen} storageKey={PANEL_STORAGE_KEY}>
          <SupplierDetailPanel
            key={panelMode === 'create' ? 'create' : selectedId}
            supplierId={selectedId}
            mode={panelMode}
            open={panelOpen}
            onOpenChange={handlePanelClose}
            onCreated={handleCreated}
            defaultKind={isAccounting ? 'other' : 'hardware'}
          />
        </SlidePanel>
      </div>
    </div>
  );
}
